/**
 * Business Unit Detail Route
 *
 * Drill-down view for a specific Business Unit showing:
 * - BU header with summary KPIs (receita, custos, margem)
 * - Products breakdown table
 * - Faturamento (revenue) transactions
 * - Custos (expenses) transactions
 * - Dimension tabs for grouping by Product, PIT, Cliente, etc.
 *
 * Route: /_auth/financial/bu-matrix/$buId
 */

import { trpc } from '@/api/trpc';
import {
  DREControlsPanel,
  calculatePeriodDates,
  type ComparisonMode,
  type DREGranularity,
  type PeriodDates,
} from '@/app/financial/dre/components';
import { getMarginCellStyles } from '@/app/financial/shared/constants';
import {
  BottomActionSheet,
  FloatingActionButton,
  type ActionItem,
} from '@/app/financial/dre/drill-down/components';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

const VALID_GRANULARITIES: DREGranularity[] = ['accumulated', 'yearly', 'quarterly', 'monthly'];

const BU_COLORS = {
  Digital: '#00BCD4',
  Promo: '#FF9800',
};

// ============================================================================
// TYPES
// ============================================================================

interface FaturamentoRow {
  nf: string | null;
  data: string | null;
  produto: string | null;
  descricao: string | null;
  pit: string | null;
  pitDescricao: string | null;
  cliente: string | null;
  valor: number;
}

interface CustoRow {
  id: number | null;
  numeroTitulo: string | null;
  data: string | null;
  fornecedor: string | null;
  pit: string | null;
  pitDescricao: string | null;
  natureza: string | null;
  valor: number;
}

interface ProdutoRow {
  codigo: string;
  descricao: string | null;
  receita: number;
  notasCount: number;
}

type TabId = 'produtos' | 'faturamento' | 'custos';

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute('/_auth/financial/bu-matrix/$buId')({
  validateSearch: (search: Record<string, unknown>): {
    startDate?: string;
    endDate?: string;
    granularity?: DREGranularity;
  } => ({
    startDate: (search.startDate as string) || undefined,
    endDate: (search.endDate as string) || undefined,
    granularity: VALID_GRANULARITIES.includes(search.granularity as DREGranularity)
      ? (search.granularity as DREGranularity)
      : undefined,
  }),
  component: BUDetailPage,
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompactCurrency(value: number): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function getDefaultDateRange(): PeriodDates {
  return calculatePeriodDates('ytd');
}

// ============================================================================
// LOADING SKELETON
// ============================================================================

function LoadingSkeleton(): JSX.Element {
  return (
    <div style={{
      padding: '2rem',
      backgroundColor: '#0d0d0d',
      minHeight: '100vh',
    }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          width: '200px',
          height: '24px',
          background: 'linear-gradient(90deg, #1a1a1a 25%, #252525 50%, #1a1a1a 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          marginBottom: '0.5rem',
        }} />
        <div style={{
          width: '300px',
          height: '36px',
          background: 'linear-gradient(90deg, #1a1a1a 25%, #252525 50%, #1a1a1a 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '8px',
        }} />
      </div>
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
      }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            flex: 1,
            height: '80px',
            background: 'linear-gradient(90deg, #1a1a1a 25%, #252525 50%, #1a1a1a 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            borderRadius: '12px',
          }} />
        ))}
      </div>
      <div style={{
        width: '100%',
        height: '400px',
        background: 'linear-gradient(90deg, #1a1a1a 25%, #252525 50%, #1a1a1a 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: '12px',
      }} />
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function BUDetailPage() {
  const navigate = useNavigate();
  const { buId } = Route.useParams();
  const searchParams = Route.useSearch();
  const defaultDates = getDefaultDateRange();

  // State
  const [startDate, setStartDate] = useState(searchParams.startDate || defaultDates.startDate);
  const [endDate, setEndDate] = useState(searchParams.endDate || defaultDates.endDate);
  const [granularity, setGranularity] = useState<DREGranularity>(searchParams.granularity || 'monthly');
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('none');
  const [activeTab, setActiveTab] = useState<TabId>('produtos');
  const [produtosSorting, setProdutosSorting] = useState<SortingState>([{ id: 'receita', desc: true }]);
  const [faturamentoSorting, setFaturamentoSorting] = useState<SortingState>([{ id: 'data', desc: true }]);
  const [custosSorting, setCustosSorting] = useState<SortingState>([{ id: 'data', desc: true }]);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  // Validate business unit
  const businessUnit = buId === 'Digital' || buId === 'Promo' ? buId : 'Digital';

  // Fetch BU detail data
  const {
    data: detailResult,
    isLoading,
    error,
    refetch,
  } = trpc.dre.getBUDetail.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
      businessUnit: businessUnit as 'Digital' | 'Promo',
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  const handleRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Computed values
  const buData = detailResult?.bu;
  const produtos = detailResult?.produtos || [];
  const faturamento = detailResult?.faturamento || [];
  const custos = detailResult?.custos || [];

  const margemPercent = buData && buData.receita > 0
    ? ((buData.margem / buData.receita) * 100)
    : 0;

  // Export handlers
  const handleExportProdutosCSV = useCallback(() => {
    const headers = ['Codigo', 'Descricao', 'Receita', 'Notas'];
    const rows = produtos.map(p => [
      p.codigo,
      p.descricao || '',
      p.receita.toFixed(2),
      p.notasCount,
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bu-${businessUnit}-produtos-${startDate}-${endDate}.csv`;
    link.click();
  }, [produtos, businessUnit, startDate, endDate]);

  const handleExportFaturamentoCSV = useCallback(() => {
    const headers = ['NF', 'Data', 'Produto', 'Descricao', 'PIT', 'Cliente', 'Valor'];
    const rows = faturamento.map(f => [
      f.nf || '',
      f.data || '',
      f.produto || '',
      f.descricao || '',
      f.pit || '',
      f.cliente || '',
      f.valor.toFixed(2),
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bu-${businessUnit}-faturamento-${startDate}-${endDate}.csv`;
    link.click();
  }, [faturamento, businessUnit, startDate, endDate]);

  const handleExportCustosCSV = useCallback(() => {
    const headers = ['Numero', 'Data', 'Fornecedor', 'PIT', 'Natureza', 'Valor'];
    const rows = custos.map(c => [
      c.numeroTitulo || '',
      c.data || '',
      c.fornecedor || '',
      c.pit || '',
      c.natureza || '',
      c.valor.toFixed(2),
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bu-${businessUnit}-custos-${startDate}-${endDate}.csv`;
    link.click();
  }, [custos, businessUnit, startDate, endDate]);

  // Mobile action handlers
  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `BU ${businessUnit} - Monutrack`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiado para a area de transferencia');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast.error('Erro ao compartilhar');
      }
    }
  }, [businessUnit]);

  const handleGoBack = useCallback(() => {
    void navigate({
      to: '/financial/bu-matrix',
      search: { startDate, endDate, granularity },
    });
  }, [navigate, startDate, endDate, granularity]);

  // Mobile action items
  const mobileActions: ActionItem[] = useMemo(() => [
    {
      label: 'Exportar Produtos',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      onClick: handleExportProdutosCSV,
    },
    {
      label: 'Exportar Faturamento',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      onClick: handleExportFaturamentoCSV,
    },
    {
      label: 'Exportar Custos',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      onClick: handleExportCustosCSV,
    },
    {
      label: 'Compartilhar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      ),
      onClick: handleShare,
    },
    {
      label: 'Voltar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      ),
      onClick: handleGoBack,
    },
  ], [handleExportProdutosCSV, handleExportFaturamentoCSV, handleExportCustosCSV, handleShare, handleGoBack]);

  // Table column helpers
  const produtoColumnHelper = createColumnHelper<ProdutoRow>();
  const faturamentoColumnHelper = createColumnHelper<FaturamentoRow>();
  const custoColumnHelper = createColumnHelper<CustoRow>();

  // Products columns
  const produtoColumns = useMemo(() => [
    produtoColumnHelper.accessor('codigo', {
      header: 'Codigo',
      cell: (info) => <span style={{ fontFamily: 'monospace', color: '#D2FA52' }}>{info.getValue()}</span>,
    }),
    produtoColumnHelper.accessor('descricao', {
      header: 'Descricao',
      cell: (info) => <span style={{ color: '#ccc' }}>{info.getValue() || '-'}</span>,
    }),
    produtoColumnHelper.accessor('notasCount', {
      header: () => <div style={{ textAlign: 'right' }}>Notas</div>,
      cell: (info) => <div style={{ textAlign: 'right', color: '#999' }}>{info.getValue()}</div>,
    }),
    produtoColumnHelper.accessor('receita', {
      header: () => <div style={{ textAlign: 'right' }}>Receita</div>,
      cell: (info) => <div style={{ textAlign: 'right', color: '#D2FA52', fontWeight: 500 }}>{formatCurrency(info.getValue())}</div>,
    }),
  ], [produtoColumnHelper]);

  // Faturamento columns
  const faturamentoColumns = useMemo(() => [
    faturamentoColumnHelper.accessor('nf', {
      header: 'NF',
      cell: (info) => <span style={{ fontFamily: 'monospace', color: '#D2FA52' }}>{info.getValue() || '-'}</span>,
    }),
    faturamentoColumnHelper.accessor('data', {
      header: 'Data',
      cell: (info) => <span style={{ color: '#999' }}>{formatDate(info.getValue())}</span>,
    }),
    faturamentoColumnHelper.accessor('produto', {
      header: 'Produto',
      cell: (info) => <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{info.getValue() || '-'}</span>,
    }),
    faturamentoColumnHelper.accessor('pit', {
      header: 'PIT',
      cell: (info) => {
        const pit = info.getValue();
        const desc = info.row.original.pitDescricao;
        return (
          <span title={desc || ''} style={{ color: '#888' }}>
            {pit || '-'}
          </span>
        );
      },
    }),
    faturamentoColumnHelper.accessor('cliente', {
      header: 'Cliente',
      cell: (info) => <span style={{ color: '#ccc' }}>{info.getValue() || '-'}</span>,
    }),
    faturamentoColumnHelper.accessor('valor', {
      header: () => <div style={{ textAlign: 'right' }}>Valor</div>,
      cell: (info) => <div style={{ textAlign: 'right', color: '#D2FA52', fontWeight: 500 }}>{formatCurrency(info.getValue())}</div>,
    }),
  ], [faturamentoColumnHelper]);

  // Custos columns
  const custosColumns = useMemo(() => [
    custoColumnHelper.accessor('numeroTitulo', {
      header: 'Numero',
      cell: (info) => <span style={{ fontFamily: 'monospace', color: '#ff6b6b' }}>{info.getValue() || '-'}</span>,
    }),
    custoColumnHelper.accessor('data', {
      header: 'Data',
      cell: (info) => <span style={{ color: '#999' }}>{formatDate(info.getValue())}</span>,
    }),
    custoColumnHelper.accessor('fornecedor', {
      header: 'Fornecedor',
      cell: (info) => <span style={{ color: '#ccc' }}>{info.getValue() || '-'}</span>,
    }),
    custoColumnHelper.accessor('pit', {
      header: 'PIT',
      cell: (info) => {
        const pit = info.getValue();
        const desc = info.row.original.pitDescricao;
        return (
          <span title={desc || ''} style={{ color: '#888' }}>
            {pit || '-'}
          </span>
        );
      },
    }),
    custoColumnHelper.accessor('natureza', {
      header: 'Natureza',
      cell: (info) => <span style={{ color: '#888', fontFamily: 'monospace', fontSize: '0.8rem' }}>{info.getValue() || '-'}</span>,
    }),
    custoColumnHelper.accessor('valor', {
      header: () => <div style={{ textAlign: 'right' }}>Valor</div>,
      cell: (info) => <div style={{ textAlign: 'right', color: '#ff6b6b', fontWeight: 500 }}>{formatCurrency(info.getValue())}</div>,
    }),
  ], [custoColumnHelper]);

  // Tables
  const produtosTable = useReactTable({
    data: produtos,
    columns: produtoColumns,
    state: { sorting: produtosSorting },
    onSortingChange: setProdutosSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const faturamentoTable = useReactTable({
    data: faturamento,
    columns: faturamentoColumns,
    state: { sorting: faturamentoSorting },
    onSortingChange: setFaturamentoSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const custosTable = useReactTable({
    data: custos,
    columns: custosColumns,
    state: { sorting: custosSorting },
    onSortingChange: setCustosSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Render table helper
  const renderTable = (table: ReturnType<typeof useReactTable<any>>, emptyMessage: string) => (
    <div style={{ overflowX: 'auto' }}>
      <table className="table-financial" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    textAlign: 'left',
                    padding: '0.75rem 0.5rem',
                    borderBottom: '2px solid #D2FA52',
                    backgroundColor: '#141414',
                    color: '#888',
                    whiteSpace: 'nowrap',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{
                    asc: ' ↑',
                    desc: ' ↓',
                  }[header.column.getIsSorted() as string] ?? null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={100} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                style={{
                  backgroundColor: row.index % 2 === 0 ? '#0d0d0d' : '#141414',
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      padding: '0.5rem',
                      borderBottom: '1px solid #1a1a1a',
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const buColor = BU_COLORS[businessUnit as keyof typeof BU_COLORS] || '#D2FA52';

  return (
    <div style={{
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#0d0d0d',
      minHeight: '100vh',
      padding: '1.5rem',
      color: '#fff',
    }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '1rem' }}>
        <Link
          to="/financial/bu-matrix"
          search={{ startDate, endDate, granularity }}
          style={{
            color: '#888',
            textDecoration: 'none',
            fontSize: '0.875rem',
          }}
        >
          ← Voltar para Matriz de BUs
        </Link>
      </div>

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <span style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: buColor,
          }} />
          <h1 style={{ margin: 0, fontSize: '1.75rem', color: buColor }}>
            {businessUnit}
          </h1>
        </div>
        <p style={{ color: '#888', margin: 0 }}>
          {businessUnit === 'Digital'
            ? 'Produtos: 3.01.0005, 3.01.0022, 3.02.0002'
            : 'Todos os outros produtos'}
        </p>
      </div>

      {/* Controls Panel */}
      <DREControlsPanel
        startDate={startDate}
        endDate={endDate}
        onPeriodChange={(dates) => {
          setStartDate(dates.startDate);
          setEndDate(dates.endDate);
        }}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onRefresh={handleRefetch}
        isLoading={isLoading}
        granularity={granularity}
        onGranularityChange={setGranularity}
        comparisonMode={comparisonMode}
        onComparisonModeChange={setComparisonMode}
      />

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(255, 107, 107, 0.1)',
          color: '#ff6b6b',
          borderRadius: '8px',
          border: '1px solid rgba(255, 107, 107, 0.3)',
          marginBottom: '1rem',
        }}>
          Erro: {error.message}
        </div>
      )}

      {/* Summary KPIs */}
      {buData && (
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}>
          <div style={{
            flex: 1,
            minWidth: '150px',
            background: '#141414',
            borderRadius: '12px',
            padding: '1rem',
            borderLeft: `4px solid ${buColor}`,
          }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.25rem' }}>Receita</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#D2FA52' }}>
              {formatCompactCurrency(buData.receita)}
            </div>
          </div>
          <div style={{
            flex: 1,
            minWidth: '150px',
            background: '#141414',
            borderRadius: '12px',
            padding: '1rem',
            borderLeft: '4px solid #ff6b6b',
          }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.25rem' }}>Custos</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#ff6b6b' }}>
              {formatCompactCurrency(buData.custos)}
            </div>
          </div>
          <div style={{
            flex: 1,
            minWidth: '150px',
            background: '#141414',
            borderRadius: '12px',
            padding: '1rem',
            borderLeft: `4px solid ${buData.margem >= 0 ? '#4caf50' : '#ff6b6b'}`,
          }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.25rem' }}>Margem</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: buData.margem >= 0 ? '#4caf50' : '#ff6b6b' }}>
              {formatCompactCurrency(buData.margem)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              {formatPercent(margemPercent)}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0',
        borderBottom: '2px solid #2a2a2a',
        marginBottom: '1rem',
      }}>
        {[
          { id: 'produtos' as const, label: `Produtos (${produtos.length})` },
          { id: 'faturamento' as const, label: `Faturamento (${faturamento.length})` },
          { id: 'custos' as const, label: `Custos (${custos.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: activeTab === tab.id ? '#D2FA52' : 'transparent',
              color: activeTab === tab.id ? '#0d0d0d' : '#888',
              fontWeight: activeTab === tab.id ? 600 : 400,
              cursor: 'pointer',
              fontSize: '0.875rem',
              borderRadius: '8px 8px 0 0',
              marginBottom: '-2px',
              borderBottom: activeTab === tab.id ? '2px solid #D2FA52' : '2px solid transparent',
              transition: 'all 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        background: '#141414',
        borderRadius: '0 0 12px 12px',
        padding: '1rem',
      }}>
        {/* Export button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={
                activeTab === 'produtos' ? handleExportProdutosCSV :
                activeTab === 'faturamento' ? handleExportFaturamentoCSV :
                handleExportCustosCSV
              }
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#D2FA52',
                color: '#0d0d0d',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Tables */}
        {activeTab === 'produtos' && renderTable(produtosTable, 'Nenhum produto encontrado')}
        {activeTab === 'faturamento' && renderTable(faturamentoTable, 'Nenhum faturamento encontrado')}
        {activeTab === 'custos' && renderTable(custosTable, 'Nenhum custo encontrado')}
      </div>

      {/* Mobile FAB and Action Sheet */}
      <FloatingActionButton
        onClick={() => setIsActionSheetOpen(true)}
        ariaLabel="Abrir menu de acoes"
      />
      <BottomActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        title="Acoes"
        actions={mobileActions}
      />
    </div>
  );
}

export default BUDetailPage;
